/**
 * The pieces `RefusalDetails` composes: two tiny coercions, a section shell,
 * and one card per payload shape the refusal `detail` can carry.
 *
 * Split out of `RefusalDetails.tsx` when adding the evidence-gate card pushed
 * that file past the 400-line hard stop in `docs/FILE_SIZE_POLICY.md`. The
 * split is along the seam that was already there: everything here renders one
 * row or one card and holds no state, while the file that imports it decides
 * which lists exist and in what order they appear.
 *
 * The doctrine of the original file carries over unchanged — everything
 * rendered is still the server's own prose. There is no local copy of the deny
 * list, no local rationale text, and no code→advice mapping.
 */
import type { AssuranceHit, DeniedHit, GateFinding } from '@/components/attest/api'

/**
 * The gate token as a noun the CPA can act on.
 *
 * This is a *label* for the check, not a restatement of policy: it names which
 * of the six gates spoke, and nothing more. The verdict, the reason and both
 * values all still come from the server. An unrecognised gate falls through to
 * its own token rather than a blank heading, so a gate added on the backend
 * degrades to a correct-if-terse label instead of an unnamed card.
 */
export const GATE_LABELS: Record<string, string> = {
  attachment: 'Attachment',
  kind: 'Document type',
  entity: 'Entity',
  period: 'Period',
  figure: 'Figure',
  preparer: 'Preparer',
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-red-900/70">{title}</p>
      <div className="mt-2 space-y-3">{children}</div>
    </div>
  )
}

export function Quoted({ text }: { text: string }) {
  if (!text) return null
  return (
    <p className="rounded border border-red-200 bg-white px-3 py-2 text-sm italic text-gray-800">
      &ldquo;{text}&rdquo;
    </p>
  )
}

export function SafeAlternative({ text }: { text: string }) {
  if (!text) return null
  return (
    <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
      <span className="font-semibold">You may say instead: </span>
      {text}
    </p>
  )
}

export function DeniedHitCard({ hit }: { hit: DeniedHit }) {
  return (
    <div className="rounded border border-red-200 bg-red-50/60 p-3">
      <p className="text-sm font-semibold text-red-900">
        {hit.label || hit.assertion_id}
        <span className="ml-2 font-mono text-xs font-normal text-red-700">{hit.assertion_id}</span>
      </p>
      <div className="mt-2 space-y-2">
        <Quoted text={hit.sentence || hit.matched_text} />
        {hit.rationale && <p className="text-sm text-gray-700">{hit.rationale}</p>}
        <SafeAlternative text={hit.safe_alternative} />
        {hit.sources?.length > 0 && (
          <ul className="list-disc pl-5 text-xs text-gray-600">
            {hit.sources.map(s => <li key={s}>{s}</li>)}
          </ul>
        )}
      </div>
    </div>
  )
}

export function AssuranceHitCard({ hit }: { hit: AssuranceHit }) {
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-3">
      <p className="text-sm font-semibold text-amber-900">
        Conveys <span className="font-mono">{hit.conveys_level}</span> assurance; this engagement
        stops at <span className="font-mono">{hit.engagement_ceiling}</span>
      </p>
      <div className="mt-2 space-y-2">
        <Quoted text={hit.sentence || hit.matched_text} />
        {hit.note && <p className="text-sm text-gray-700">{hit.note}</p>}
      </div>
    </div>
  )
}

/**
 * One evidence gate's finding: which check failed, and the two values that
 * disagreed.
 *
 * Before this card the wall said only *that* the evidence did not verify. The
 * CPA's next move differs entirely depending on which gate spoke — a wrong
 * entity is a wrong attachment, a wrong period is a re-export, a missing figure
 * is usually a subtotal the letter names differently — and none of that was on
 * the screen. The gate name is the first thing here for that reason.
 *
 * `observed` and `expected` are rendered side by side and labelled by where
 * they came from, not by whether they are right: the document said one thing,
 * the statement claimed another, and this component takes no view on which of
 * the two is the mistake. Both are the server's strings, verbatim. The pair is
 * only drawn when at least one is non-empty — a gate that blocks because there
 * is no document at all has nothing to compare, and an empty two-column grid
 * would read as "the document said nothing", which is a different finding.
 */
export function GateFindingCard({ finding }: { finding: GateFinding }) {
  const review = finding.outcome === 'review'
  const tone = review
    ? 'border-amber-300 bg-amber-50'
    : 'border-red-200 bg-red-50/60'
  const chipTone = review ? 'bg-amber-200 text-amber-900' : 'bg-red-900 text-white'
  const hasPair = Boolean(finding.observed || finding.expected)

  return (
    <div className={`rounded border p-3 ${tone}`} data-testid={`gate-finding-${finding.gate}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${chipTone}`}>
          {GATE_LABELS[finding.gate] ?? finding.gate}
        </span>
        {finding.assertion_id && (
          <span className="font-mono text-xs text-gray-700">{finding.assertion_id}</span>
        )}
        {finding.code && (
          <span className="rounded bg-white/70 px-2 py-0.5 font-mono text-[11px] text-gray-700">
            {finding.code}
          </span>
        )}
      </div>

      {finding.reason && <p className="mt-2 text-sm text-gray-800">{finding.reason}</p>}

      {hasPair && (
        <dl className="mt-2 grid gap-2 sm:grid-cols-2">
          <div className="rounded border border-gray-200 bg-white px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              The document said
            </dt>
            <dd
              className="mt-1 break-words text-sm text-gray-900"
              data-testid={`gate-observed-${finding.gate}`}
            >
              {finding.observed || <span className="text-gray-500">nothing on this point</span>}
            </dd>
          </div>
          <div className="rounded border border-gray-200 bg-white px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              The statement claimed
            </dt>
            <dd
              className="mt-1 break-words text-sm text-gray-900"
              data-testid={`gate-expected-${finding.gate}`}
            >
              {finding.expected || <span className="text-gray-500">nothing on this point</span>}
            </dd>
          </div>
        </dl>
      )}

      {finding.reference && (
        <p className="mt-2 text-xs text-gray-600">
          <span className="font-semibold">Read from: </span>
          <span className="break-all font-mono">{finding.reference}</span>
        </p>
      )}
    </div>
  )
}

/** The allow-list gate's `problems` (objects) and the eligibility gate's (strings). */
export function ProblemList({ problems }: { problems: unknown[] }) {
  return (
    <ul className="space-y-2">
      {problems.map((p, i) => {
        if (typeof p === 'string') {
          return <li key={i} className="text-sm text-gray-800">{p}</li>
        }
        const row = (p ?? {}) as Record<string, unknown>
        return (
          <li key={i} className="rounded border border-red-200 bg-red-50/60 p-3">
            <p className="text-sm text-red-900">
              <span className="font-mono text-xs">{str(row.assertion_id)}</span>{' '}
              {str(row.reason)}
            </p>
            <div className="mt-2"><Quoted text={str(row.text)} /></div>
          </li>
        )
      })}
    </ul>
  )
}
